import { useInstitution } from '@shared/composables/useInstitution'
import { isTransferbankAdminUser } from '@shared/composables/useWhitelabelService.js'
import { WebSocketClient, SOCKET_STATE, SOCKET_EVENT } from '@shared/services/WebsocketClient'
import { handleGlobalError } from '@shared/services/errorHandler'
import { useAuthToken } from '@shared/stores/authToken.js'
import { useWss } from '@shared/stores/wss.js'

export function useWebSocket() {
  const { institutionType } = useInstitution()
  const interests = new Set()
  const conectarStag = `${import.meta.env.VITE_APP_WSS}?token=${useAuthToken().getToken}`

  const setInterests = () => {
    interests.add('/topics/exchanges/USD')
    interests.add('/topics/exchanges/GBP')
    interests.add('/topics/exchanges/SEK')
    interests.add('/topics/exchanges/EUR')
    interests.add('/topics/exchanges/JPY')
    interests.add('/topics/exchanges/CAD')
    interests.add('/topics/exchanges/CHF')
    interests.add('/topics/exchanges/AUD')
    interests.add('/topics/exchanges/AED')
    interests.add('/topics/exchanges/CNY')
    interests.add('/topics/exchanges/USDT')
    interests.add('/topics/exchanges/USDC')
    interests.add('/topics/market/status')
    interests.add('/topics/ptaxes')
    setRefreshExchangeAdmin()
  }

  const setRefreshExchangeAdmin = () => {
    try {
      const topics = ['operations/pre', 'operations/pos', 'onboardings/step2']
      if (isTransferbankAdminUser()) {
        const institutions = Object.keys(institutionType.value)
          .filter((key) => key && key.trim() !== '')
          .map((key) => key.toLowerCase())
        for (const institution of institutions) {
          for (const topic of topics) {
            interests.add(`/topics/admins/institutions/${institution}/${topic}`)
          }
        }
      } else {
        const institution = useAuthToken().getInstitution.toLowerCase()
        for (const topic of topics) {
          interests.add(`/topics/admins/institutions/${institution}/${topic}`)
        }
      }
    } catch (e) {
      handleGlobalError(e, 'Erro connectRefreshExchangeAdmin:')
    }
  }

  const client = new WebSocketClient(conectarStag, {
    withDebug: false,
    withHealthCheck: true,
    withPingPongProtocol: true,
    withReconnection: true
  })

  client.on(SOCKET_STATE.CONNECTED, () => {
    try {
      for (const topic of interests) {
        client.sendMessage({
          action: 'subscribe',
          channel: topic
        })
      }
    } catch (e) {
      handleGlobalError(e, 'Erro ao subscribar após CONNECTED:')
    }
  })

  client.on(SOCKET_EVENT.ON_MESSAGE_JSON, (message) => {
    if (message?.action === 'subscribe') return
    if (message?.data?.channel === '/topics/market/status') {
      useWss().market = message.data.marketOpen
      return
    }
    if (message?.data?.channel === '/topics/ptaxes') {
      useWss().PTAX = message.data
      return
    }
    const channel = message?.data?.channel
    const exchangeOpenRegex = new RegExp(`/topics/admins/.*/operations/pre`)
    const exchangeCloseRegex = new RegExp(`/topics/admins/.*/operations/pos`)
    const newClientRegex = new RegExp(`/topics/admins/.*/onboardings/step2`)

    const { id, clientId, clientName, principalAmount, type, currencyType } = message.data
    const content = { id: id || clientId, clientName, principalAmount, type, currencyType }

    if (exchangeOpenRegex.test(channel)) {
      useWss().newPreOperation = content
    } else if (exchangeCloseRegex.test(channel)) {
      useWss().newPosOperation = content
    } else if (newClientRegex.test(channel)) {
      useWss().newClient = content
    }
  })

  client.on(SOCKET_EVENT.ON_MESSAGE, (payload) => {
    try {
      const data = payload.data
      const exchangeRates = useWss().getExchangeRate || {}

      const splits = data.split('|')
      const currency = splits[3]

      const dateTimeRegex = /(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})/
      const newDateTime = new Date(splits[5].match(dateTimeRegex)[0])

      if (exchangeRates[currency] == null) {
        exchangeRates[currency] = data
        useWss().exchangeRate = exchangeRates
        return
      }

      const localSplits = exchangeRates[currency].split('|')
      const localDateTime = new Date(localSplits[5].match(dateTimeRegex)[0])

      if (newDateTime > localDateTime) {
        exchangeRates[currency] = data
        useWss().exchangeRate = exchangeRates
      }
    } catch (e) {
      handleGlobalError(e, 'Erro processExchangeRate:')
    }
  })

  const startWebSocketConnections = () => {
    try {
      setInterests()
      client.connect()
    } catch (e) {
      handleGlobalError(e, 'Erro start websocket:')
    }
  }

  const disconnectWebSocketConnections = () => {
    try {
      client.disconnect()
      interests.clear()
    } catch (e) {
      handleGlobalError(e, 'Erro stop websocket:')
    }
  }

  return { startWebSocketConnections, disconnectWebSocketConnections }
}
